import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Form, Button, Tag, Tabs, TabsProps, Spin, theme, Image } from "antd";
import {
  SaveOutlined,
  CopyOutlined,
  MailOutlined,
  ExportOutlined,
  CheckCircleOutlined,
  LoadingOutlined,
  EditOutlined,
  CloseOutlined,
  UserOutlined,
  BankOutlined,
  CloudUploadOutlined,
} from "@ant-design/icons";
import { PageLayout } from "../../Common/Layout";
import { adminContext } from "../../Common/PageRoute";
import PracticeDataRequestService, {
  CsvTemplateType,
  EmailProviderType,
  UserCreationMethod,
  getEnumTypeValue,
  PracticeDataRequestType,
} from "../../Services/PracticeDataRequestService";
import { Utils } from "../../Utilities/Utils";
import { sortDataConversionFiles } from "./PracticeDataRequestFileUtils";
import PracticeDataRequestContactInfo from "./PracticeDataRequestContactInfo";
import PracticeDataRequestBrandingAssets from "./PracticeDataRequestBrandingAssets";
import PracticeDataRequestInvoiceTemplates from "./PracticeDataRequestInvoiceTemplates";
import PracticeDataRequestFiles from "./PracticeDataRequestFiles";
import PracticeDataRequestProgressSteps from "./PracticeDataRequestProgressSteps";

interface DataConversionFile {
  Name: string;
  FileUrl: string;
  Type: CsvTemplateType;
}

interface PracticeDataRequestFormValues {
  practiceName?: string;
  practicePhoneNumber?: string;
  publicEmailNoReply?: string;
  customNoReplyEmail?: string;
  emailProvider?: EmailProviderType | number;
  userCreationMethod?: UserCreationMethod | number;
  website?: string;
  proposedUrl?: string;
  contactPersonName?: string;
  contactPersonEmail?: string;
  contactPersonPhone?: string;
  clientEmail?: string;
  logoUrl?: string;
  faviconUrl?: string;
  invoiceSampleHeaderUrl?: string;
  invoiceSampleFooterUrl?: string;
  status?: number;
  dataConversionFiles?: DataConversionFile[];
}

// Function to render tab labels with icons and counts
const renderTabLabel = (
  icon: React.ReactElement,
  text: string,
  active: boolean,
  token: any,
  count?: number,
) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "4px 8px",
    }}
  >
    {React.cloneElement(icon, {
      style: {
        fontSize: 15,
        color: active ? token.colorPrimary : token.colorTextSecondary,
      },
    })}
    <span style={{ fontWeight: active ? 600 : 400 }}>{text}</span>
    {count !== undefined && count > 0 && (
      <span
        style={{
          background: active ? token.colorPrimary : token.colorFillSecondary,
          color: active ? "#fff" : token.colorTextSecondary,
          fontSize: 11,
          fontWeight: 700,
          borderRadius: 10,
          padding: "0 6px",
          lineHeight: "16px",
          display: "inline-block",
        }}
      >
        {count}
      </span>
    )}
  </div>
);

const PracticeDataRequestDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { openNotification } = React.useContext(adminContext);
  const { token } = theme.useToken();
  const [form] = Form.useForm<PracticeDataRequestFormValues>();
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [requestData, setRequestData] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<string>("setup");
  const dataConversionFiles = Form.useWatch("dataConversionFiles", form) || [];
  const [rawAssetFiles, setRawAssetFiles] = useState<{ [key: string]: File }>(
    {},
  );

  const getUrlString = (val: any): string => {
    if (typeof val === "string") return val;
    if (val && typeof val.path === "string") return val.path;
    if (val && typeof val.Path === "string") return val.Path;
    return "";
  };

  const getAdminAssetPreviewUrl = (val: any, assetKey: string): string => {
    if (!val) return "";
    const raw: string = getUrlString(val);
    if (!raw || raw.trim() === "") return "";
    if (raw.startsWith("blob:") || raw.startsWith("data:")) return raw;
    if (raw.includes("AO_Header.jpg") || raw.includes("AO_Footer.jpg")) {
      return raw.startsWith("http")
        ? raw
        : `${window.location.origin}/${raw.replace(/^\//, "")}`;
    }
    const currentId = String(id || requestData?.id || requestData?.Id || "");
    if (currentId) {
      const fileName = raw.split("/").pop() || "";
      return `/api/PracticeDataRequests/${currentId}/Asset/${assetKey}?v=${encodeURIComponent(fileName)}`;
    }
    return raw;
  };

  // Fetch practice data request details
  const fetchRequestDetails = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await PracticeDataRequestService.getPracticeDataRequestById(
        String(id),
      );
      if (res?.status && res?.result) {
        const data = res.result;
        setRequestData(data);

        if (data.referenceNumber && id) {
          (window as any).dynamicPathLabels = {
            ...((window as any).dynamicPathLabels || {}),
            [id]: data.referenceNumber,
          };
          window.dispatchEvent(new CustomEvent("dynamicPathLabelsUpdate"));
        }

        // Normalize dataConversionFiles
        const parsedFiles = (data.dataConversionFiles || []).map(
          (file: any) => ({
            name: file.name || file.Name || "",
            type:
              typeof file.type === "number"
                ? file.type
                : typeof file.Type === "number"
                  ? file.Type
                  : getEnumTypeValue(file.type || file.Type),
            file: {
              name: file.name || file.Name || "",
              path:
                file.file?.path ||
                file.File?.Path ||
                file.fileUrl ||
                file.FileUrl ||
                "",
            },
          }),
        );

        const formValues: PracticeDataRequestFormValues = {
          practiceName: data.practiceName || "",
          practicePhoneNumber: data.practicePhoneNumber || "",
          publicEmailNoReply: data.publicEmailNoReply || "",
          customNoReplyEmail: data.customNoReplyEmail || "",
          emailProvider: data.emailProvider ?? EmailProviderType.MicrosoftOutlook,
          userCreationMethod:
            data.userCreationMethod ?? UserCreationMethod.UploadCsv,
          website: data.website || "",
          proposedUrl: data.proposedUrl || "",
          contactPersonName: data.contactPersonName || "",
          contactPersonEmail: data.contactPersonEmail || "",
          contactPersonPhone: data.contactPersonPhone || "",
          clientEmail: data.clientEmail || "",
          logoUrl: getUrlString(data.logoUrl || data.LogoUrl),
          faviconUrl: getUrlString(data.faviconUrl || data.FaviconUrl),
          invoiceSampleHeaderUrl: getUrlString(
            data.invoiceSampleHeaderUrl || data.InvoiceSampleHeaderUrl,
          ),
          invoiceSampleFooterUrl: getUrlString(
            data.invoiceSampleFooterUrl || data.InvoiceSampleFooterUrl,
          ),
          status: data.status,
          dataConversionFiles: sortDataConversionFiles(parsedFiles) as any,
        };

        form.setFieldsValue(formValues);
      } else {
        openNotification(
          "error",
          res?.message || "Failed to load request details.",
        );
      }
    } catch {
      openNotification("error", "Error loading practice data request details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequestDetails();
    return () => {
      if (id && (window as any).dynamicPathLabels?.[id]) {
        delete (window as any).dynamicPathLabels[id];
        window.dispatchEvent(new CustomEvent("dynamicPathLabelsUpdate"));
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Asset upload helper
  const handleAssetUpload = (field: any, file: File) => {
    const isImage =
      file.type === "image/png" ||
      file.type === "image/jpeg" ||
      file.type === "image/svg+xml" ||
      file.type === "image/x-icon" ||
      file.type === "image/vnd.microsoft.icon" ||
      file.name.endsWith(".ico") ||
      file.name.endsWith(".svg");
    if (!isImage) {
      openNotification(
        "error",
        "You can only upload image files (PNG, JPG, SVG, ICO).",
      );
      return;
    }
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      openNotification("error", "Image must be smaller than 5MB.");
      return;
    }

    setRawAssetFiles((prev) => ({ ...prev, [field]: file }));
    const objectUrl = URL.createObjectURL(file);
    form.setFieldValue(field, objectUrl);
  };

  // Resend email
  const handleResendEmail = async () => {
    if (!requestData?.clientEmail) {
      openNotification("error", "No email address found for this request.");
      return;
    }
    try {
      const res = await PracticeDataRequestService.resendEmail(
        requestData.clientEmail,
      );
      if (res?.status) {
        openNotification(
          "success",
          res.message || "Email invitation resent successfully!",
        );
      } else {
        openNotification(
          "error",
          res?.message || "Failed to resend email invitation.",
        );
      }
    } catch {
      openNotification("error", "Error resending email invitation.");
    }
  };

  // Copy public link
  const handleCopyLink = () => {
    if (!requestData?.token) {
      openNotification("warning", "No access token found for this request.");
      return;
    }
    const url = `${window.location.origin}/data-request/${requestData.token}`;
    navigator.clipboard.writeText(url);
    openNotification("success", "Request link copied to clipboard!");
  };

  // Enter / Cancel edit mode
  const enterEditMode = () => {
    setIsEditing(true);
  };

  const cancelEditMode = () => {
    setIsEditing(false);
    setRawAssetFiles({});
    if (requestData) {
      const parsedFiles = (requestData.dataConversionFiles || []).map(
        (file: any) => ({
          name: file.name || file.Name || "",
          type:
            typeof file.type === "number"
              ? file.type
              : typeof file.Type === "number"
                ? file.Type
                : getEnumTypeValue(file.type || file.Type),
          file: {
            name: file.name || file.Name || "",
            path:
              file.file?.path ||
              file.File?.Path ||
              file.fileUrl ||
              file.FileUrl ||
              "",
          },
        }),
      );

      form.setFieldsValue({
        practiceName: requestData.practiceName || "",
        practicePhoneNumber: requestData.practicePhoneNumber || "",
        publicEmailNoReply: requestData.publicEmailNoReply || "",
        customNoReplyEmail: requestData.customNoReplyEmail || "",
        emailProvider:
          requestData.emailProvider ?? EmailProviderType.MicrosoftOutlook,
        userCreationMethod:
          requestData.userCreationMethod ?? UserCreationMethod.UploadCsv,
        website: requestData.website || "",
        proposedUrl: requestData.proposedUrl || "",
        contactPersonName: requestData.contactPersonName || "",
        contactPersonEmail: requestData.contactPersonEmail || "",
        contactPersonPhone: requestData.contactPersonPhone || "",
        clientEmail: requestData.clientEmail || "",
        logoUrl: getUrlString(requestData.logoUrl || requestData.LogoUrl),
        faviconUrl: getUrlString(
          requestData.faviconUrl || requestData.FaviconUrl,
        ),
        invoiceSampleHeaderUrl: getUrlString(
          requestData.invoiceSampleHeaderUrl ||
            requestData.InvoiceSampleHeaderUrl,
        ),
        invoiceSampleFooterUrl: getUrlString(
          requestData.invoiceSampleFooterUrl ||
            requestData.InvoiceSampleFooterUrl,
        ),
        status: requestData.status,
        dataConversionFiles: sortDataConversionFiles(parsedFiles) as any,
      });
    }
  };

  // Save handler
  const handleSave = async (overrideStatus?: number) => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const payload = {
        id: String(id || requestData?.id || requestData?.Id),
        practiceName: values.practiceName || "",
        practicePhoneNumber: values.practicePhoneNumber || "",
        publicEmailNoReply: values.publicEmailNoReply || "",
        customNoReplyEmail: values.customNoReplyEmail || "",
        emailProvider: values.emailProvider,
        userCreationMethod: values.userCreationMethod,
        website: values.website || "",
        proposedUrl: values.proposedUrl || "",
        contactPersonName: values.contactPersonName || "",
        contactPersonEmail: values.contactPersonEmail || "",
        contactPersonPhone: values.contactPersonPhone || "",
        clientEmail: values.clientEmail || requestData?.clientEmail || "",
        logoUrl: rawAssetFiles["logoUrl"]
          ? ""
          : getUrlString(values.logoUrl || requestData?.logoUrl),
        faviconUrl: rawAssetFiles["faviconUrl"]
          ? ""
          : getUrlString(values.faviconUrl || requestData?.faviconUrl),
        invoiceSampleHeaderUrl: rawAssetFiles["invoiceSampleHeaderUrl"]
          ? ""
          : getUrlString(
              values.invoiceSampleHeaderUrl ||
                requestData?.invoiceSampleHeaderUrl,
            ),
        invoiceSampleFooterUrl: rawAssetFiles["invoiceSampleFooterUrl"]
          ? ""
          : getUrlString(
              values.invoiceSampleFooterUrl ||
                requestData?.invoiceSampleFooterUrl,
            ),
        status:
          overrideStatus !== undefined
            ? overrideStatus
            : values.status ?? requestData?.status,
        dataConversionFiles: (values.dataConversionFiles || []).map(
          (item: any) => ({
            name: item.name,
            type:
              typeof item.type === "number"
                ? item.type
                : getEnumTypeValue(item.type),
            fileUrl: getUrlString(item.file?.path || item.fileUrl || item.file),
          }),
        ),
      };

      const formData = new FormData();
      formData.append("data", JSON.stringify(payload));
      if (rawAssetFiles["logoUrl"])
        formData.append("logo", rawAssetFiles["logoUrl"]);
      if (rawAssetFiles["faviconUrl"])
        formData.append("favicon", rawAssetFiles["faviconUrl"]);
      if (rawAssetFiles["invoiceSampleHeaderUrl"])
        formData.append("invoicesampleheader", rawAssetFiles["invoiceSampleHeaderUrl"]);
      if (rawAssetFiles["invoiceSampleFooterUrl"])
        formData.append("invoicesamplefooter", rawAssetFiles["invoiceSampleFooterUrl"]);

      (values.dataConversionFiles || []).forEach((item: any, idx: number) => {
        if (item.rawFile) {
          formData.append(`ConversionFile_${idx}`, item.rawFile);
        }
      });

      const res = await PracticeDataRequestService.updatePracticeDataRequest(
        formData,
      );

      if (res?.status) {
        openNotification(
          "success",
          res.message || "Practice details updated successfully!",
        );
        setIsEditing(false);
        setRawAssetFiles({});
        fetchRequestDetails();
      } else {
        openNotification(
          "error",
          res?.message || "Failed to update practice details.",
        );
      }
    } catch {
      openNotification(
        "error",
        "Please resolve form errors before saving.",
      );
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status?: number | string) => {
    switch (status) {
      case 2:
      case "Submitted":
        return <Tag color="success" style={{ borderRadius: 4, margin: 0 }}>Submitted</Tag>;
      case 3:
      case "Approved":
        return <Tag color="processing" style={{ borderRadius: 4, margin: 0 }}>Approved</Tag>;
      case 4:
      case "Rejected":
        return <Tag color="error" style={{ borderRadius: 4, margin: 0 }}>Rejected</Tag>;
      case 1:
      case "Pending":
      default:
        return <Tag color="warning" style={{ borderRadius: 4, margin: 0 }}>Pending</Tag>;
    }
  };

  const isInternal =
    requestData?.requestType === PracticeDataRequestType.Internal ||
    requestData?.requestType === 2;

  const filesCount = dataConversionFiles?.length || 0;
  const isPendingStatus =
    requestData?.status === 1 || requestData?.status === "Pending";

  const currentLogoVal =
    form.getFieldValue("logoUrl") ||
    requestData?.logoUrl ||
    requestData?.LogoUrl;
  const logoPreviewUrl = getAdminAssetPreviewUrl(currentLogoVal, "logo");

  const titleText =
    requestData?.practiceName ||
    (requestData?.referenceNumber
      ? `Practice Data Request (${requestData.referenceNumber})`
      : "Practice Data Request");

  const refText = requestData?.referenceNumber || "PDR";

  // 2 Segments: Practice & Portal Setup and Data Imports & Templates
  const tabItems: TabsProps["items"] = [
    {
      key: "setup",
      label: renderTabLabel(
        <BankOutlined />,
        "Practice & Portal Setup",
        activeTab === "setup",
        token,
      ),
      children: (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <PracticeDataRequestContactInfo
            requestData={requestData}
            isEditing={isEditing}
          />
          <PracticeDataRequestBrandingAssets
            form={form}
            isEditing={isEditing}
            getAdminAssetPreviewUrl={getAdminAssetPreviewUrl}
            onAssetUpload={handleAssetUpload}
          />
          <PracticeDataRequestInvoiceTemplates
            form={form}
            isEditing={isEditing}
            getAdminAssetPreviewUrl={getAdminAssetPreviewUrl}
            onAssetUpload={handleAssetUpload}
          />
        </div>
      ),
    },
    {
      key: "imports",
      label: renderTabLabel(
        <CloudUploadOutlined />,
        "Data Imports & Templates",
        activeTab === "imports",
        token,
        filesCount,
      ),
      children: (
        <PracticeDataRequestFiles form={form} isEditing={isEditing} />
      ),
    },
  ];

  return (
    <PageLayout>
      <Form
        form={form}
        layout="vertical"
        initialValues={{ status: 1 }}
        disabled={!isEditing}
      >
        {/* Practice Header Card (matches PracticeDetails hero card) */}
        <div
          style={{
            background: token.colorBgContainer,
            borderRadius: token.borderRadiusLG,
            padding: "16px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 16,
            minHeight: 88,
            boxShadow: "none",
          }}
        >
          {/* Left: Logo & Details */}
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div
              style={{
                width: 72,
                height: 72,
                borderRadius: token.borderRadiusLG,
                background: token.colorBgContainer,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                flexShrink: 0,
                overflow: "hidden",
                border: `1px solid ${token.colorBorderSecondary}`,
                padding: 4,
                boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
              }}
            >
              {logoPreviewUrl ? (
                <Image
                  preview={false}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                  }}
                  src={logoPreviewUrl}
                  alt="logo"
                />
              ) : (
                <UserOutlined
                  style={{ fontSize: 32, color: token.colorTextSecondary }}
                />
              )}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  flexWrap: "wrap",
                }}
              >
                <span
                  style={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: token.colorText,
                    lineHeight: "24px",
                  }}
                >
                  {titleText}
                </span>
                <Tag
                  color="default"
                  style={{
                    borderRadius: 4,
                    margin: 0,
                    fontWeight: 600,
                    color: token.colorTextSecondary,
                  }}
                >
                  Ref: {refText}
                </Tag>
                <Tag
                  color={isInternal ? "purple" : "blue"}
                  style={{ borderRadius: 4, margin: 0, fontWeight: 600 }}
                >
                  {isInternal ? "Internal Request" : "External Request"}
                </Tag>
                {getStatusBadge(requestData?.status)}
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                  fontSize: 12,
                  color: token.colorTextSecondary,
                  flexWrap: "wrap",
                }}
              >
                {requestData?.proposedUrl && (
                  <span>
                    Workspace URL:{" "}
                    <strong style={{ color: token.colorText }}>
                      {requestData.proposedUrl}
                    </strong>
                  </span>
                )}
                {requestData?.clientEmail && (
                  <span>
                    Invite Email:{" "}
                    <strong style={{ color: token.colorText }}>
                      {requestData.clientEmail}
                    </strong>
                  </span>
                )}
                {requestData?.createdDate && (
                  <span>
                    Created:{" "}
                    <strong style={{ color: token.colorText }}>
                      {Utils.getFormattedDate(requestData.createdDate)}
                    </strong>
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Right: Quick Stats & Actions */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 12px",
                background: token.colorFillAlter,
                borderRadius: token.borderRadiusSM,
                border: `1px solid ${token.colorBorderSecondary}`,
                cursor: "pointer",
              }}
              onClick={() => setActiveTab("imports")}
              title="View Data Conversion Files"
            >
              <span
                style={{
                  fontSize: 12,
                  color: token.colorTextSecondary,
                  fontWeight: 500,
                }}
              >
                Files:
              </span>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: token.colorPrimary,
                }}
              >
                {filesCount}
              </span>
            </div>

            {!isInternal && requestData?.token && (
              <Button
                type="default"
                icon={<ExportOutlined />}
                onClick={() =>
                  window.open(
                    `/data-request/${requestData.token}`,
                    "_blank",
                    "noopener,noreferrer",
                  )
                }
                style={{
                  borderRadius: token.borderRadiusSM,
                  fontWeight: 500,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                Open Form
              </Button>
            )}

            {!isInternal && requestData?.token && (
              <Button
                type="default"
                icon={<CopyOutlined />}
                onClick={handleCopyLink}
                style={{
                  borderRadius: token.borderRadiusSM,
                  fontWeight: 500,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                Copy Link
              </Button>
            )}

            {!isInternal && (
              <Button
                type="default"
                icon={<MailOutlined />}
                onClick={handleResendEmail}
                style={{
                  borderRadius: token.borderRadiusSM,
                  fontWeight: 500,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                Resend Email
              </Button>
            )}

            {isInternal && isPendingStatus && (
              <Button
                type="default"
                icon={<CheckCircleOutlined />}
                loading={saving}
                onClick={() => handleSave(2)}
                style={{
                  borderRadius: token.borderRadiusSM,
                  fontWeight: 500,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                Mark as Submitted
              </Button>
            )}

            {!isEditing ? (
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={enterEditMode}
                style={{
                  borderRadius: token.borderRadiusSM,
                  fontWeight: 500,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                Edit Details
              </Button>
            ) : (
              <>
                <Button
                  type="default"
                  icon={<CloseOutlined />}
                  onClick={cancelEditMode}
                  style={{
                    borderRadius: token.borderRadiusSM,
                    fontWeight: 500,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  loading={saving}
                  onClick={() => handleSave()}
                  style={{
                    borderRadius: token.borderRadiusSM,
                    fontWeight: 500,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  Save Changes
                </Button>
              </>
            )}
          </div>
        </div>

        {!isInternal && (
          <div style={{ marginTop: 12 }}>
            <PracticeDataRequestProgressSteps requestData={requestData} />
          </div>
        )}

        <div style={{ marginTop: 12 }}>
          <Spin
            spinning={loading}
            indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />}
          >
            <Tabs
              activeKey={activeTab}
              size="small"
              items={tabItems}
              className="custom-tabs"
              onChange={(key) => setActiveTab(key)}
            />
          </Spin>
        </div>
      </Form>
    </PageLayout>
  );
};

export default PracticeDataRequestDetails;
